/**
 * @fileoverview Eurofarma Gestao Lena Dashboard page component
 * Displays statistics, quick access buttons, and alerts for Eurofarma management users
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, intl, HTTP_STATUS, ROUTES } from "dmsbasereact";

import { DashboardLayout } from "../../components/Dashboard";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";


// MUI Icons
import DescriptionIcon from "@mui/icons-material/Description";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import GroupIcon from "@mui/icons-material/Group";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import SettingsIcon from "@mui/icons-material/Settings";
/**
 * EurofarmaGestaoLenaDashboard - Dashboard page for Eurofarma management users
 * @returns {JSX.Element} The Eurofarma dashboard with stats, quick access, and alerts
 */
export const EurofarmaGestaoLenaDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [alerts, setAlerts] = useState(null);

    /**
     * Fetch all dashboard data from API endpoints
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsResponse, alertsResponse] = await Promise.all([
                fetchData("/dashboard/eurofarma/stats", "GET", true),
                fetchData("/dashboard/eurofarma/alerts", "GET", true),
            ]);

            if (statsResponse.status === HTTP_STATUS.OK_200) {
                setStats(statsResponse.data?.data);
            }

            if (alertsResponse.status === HTTP_STATUS.OK_200) {
                setAlerts(alertsResponse.data?.data);
            }
        } catch (err) {
            console.error("Error fetching Eurofarma dashboard data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    /**
     * Format pending returns alerts
     */
    const formatPendingReturnsAlerts = () => {
        if (!alerts?.pending_returns) return [];
        return alerts.pending_returns.map((item) => ({
            id: item.return_pk,
            title: item.organization,
            description: `${item.quantity} ${intl.get("Capsules").d("capsules")}`,
            return_pk: item.return_pk,
        }));
    };

    /**
     * Format recent dispensations alerts
     */
    const formatRecentDispensationsAlerts = () => {
        if (!alerts?.recent_dispensations) return [];
        return alerts.recent_dispensations.map((item) => ({
            id: item.dispensation_pk,
            title: item.patient_name,
            description: new Date(item.dispensation_date).toLocaleDateString('pt-BR'),
            dispensation_pk: item.dispensation_pk,
        }));
    };
    
    
    /**
     * Handle click on pending return alert
     */
    const handleReturnClick = (item) => {
        if (item?.return_pk) {
            navigate(APP_ROUTES.Return);
        }
    };

    /**
     * Handle click on recent dispensation alert
     */
    const handleDispensationClick = (item) => {
        if (item?.dispensation_pk) {
            navigate(APP_ROUTES.Dispensing);
        }
    };
    
    // Stats configuration
    const statsConfig = [
        {
            title: intl.get("TotalPrescriptions").d("Total Prescriptions"),
            value: stats?.total_prescriptions || 0,
            icon: DescriptionIcon,
            color: APP_COLORS.primary.main,
        },
        {
            title: intl.get("ActivePrescriptions").d("Active Prescriptions"),
            value: stats?.active_prescriptions || 0,
            icon: EventAvailableIcon,
            color: APP_COLORS.system.success,
        },
        {
            title: intl.get("TotalDispensations").d("Total Dispensations"),
            value: stats?.total_dispensations || 0,
            icon: VaccinesIcon,
            color: APP_COLORS.system.info,
        },
        {
            title: intl.get("PendingReturns").d("Pending Returns"),
            value: stats?.pending_returns || 0,
            icon: AssignmentReturnIcon,
            color: APP_COLORS.system.warning,
        },
        
    ];

    // Quick access buttons configuration
    const quickAccessConfig = [
        {
            label: intl.get("AccountApproval").d("Account Approval"),
            icon: HowToRegIcon,
            route: APP_ROUTES.InstitutionApproval,
            color: APP_COLORS.primary.main,
        },
        {
            label: intl.get("UserManagement").d("User Management"),
            icon: GroupIcon,
            route: APP_ROUTES.UserManagement,
            color: APP_COLORS.secondary.main,
        },
        {
            label: intl.get("Analytics").d("Analytics"),
            icon: AutoGraphIcon,
            route: APP_ROUTES.Insights,
            color: APP_COLORS.system.info,
        },
        {
            label: intl.get("Settings").d("Settings"),
            icon: SettingsIcon,
            route: ROUTES.SystemSettings,
            color: APP_COLORS.system.warning,
        },
    ];

    // Alerts configuration
    const alertsConfig = [
        {
            title: intl.get("PendingReturns").d("Pending Returns"),
            items: formatPendingReturnsAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoPendingReturns").d("No pending returns"),
            onItemClick: handleReturnClick,
        },
        {
            title: intl.get("RecentDispensations").d("Recent Dispensations"),
            items: formatRecentDispensationsAlerts(),
            severity: "info",
            emptyMessage: intl.get("NoRecentDispensations").d("No recent dispensations"),
            onItemClick: handleDispensationClick,
        },
    ];

    return (
        <DashboardLayout
            welcomeName={stats?.user_name}
            loading={loading}
            stats={statsConfig}
            quickAccess={quickAccessConfig}
            alerts={alertsConfig}
        />
    );
};
